import React, { useState, useRef, useEffect } from 'react';
import { markDayCompleted, isDayCompleted, saveSubmissionData, getScore, getTotalStars, getDayScore, getDayStars } from '../utils/completionStorage';
import { getAuthHeaders } from '../utils/auth';
import { SimpleFileUpload } from 'simple-file-upload-react';
import { trackSubmitResponse } from '../utils/analytics';
import { sound } from '../utils/sound';
import ChatInterface from './ChatInterface';

interface ScoreBreakdown {
  relevance: number;
  correctness: number;
  depth: number;
  clarity: number;
  promptcraft: number;
}

interface Feedback {
  improvements?: string[];
  strengths?: string[];
  summary?: string;
}

interface BonusData {
  totalPoints: number;
  stars?: number;
  scoreBreakdown?: ScoreBreakdown;
  scoreDelta?: number;
  addedPoints?: number; // The actual points added to total score
  isImprovement?: boolean; // Whether this is an improvement over previous submission
  previousBreakdown?: ScoreBreakdown | null; // Previous breakdown for comparison
  feedback?: Feedback;
}

interface ContentFlags {
  response: boolean;
  promptUsed: boolean;
  files: boolean;
  transcript: boolean;
}

interface ChatSession {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  conversationId: number | null;
}

interface AnswerSubmissionProps {
  dayNumber: number;
  isCurrentDay: boolean;
  isSpecialDay?: boolean;
  companyGameId?: number;
  totalAvailableDays?: number;
  availableDays?: number[];
  onServerAnswer?: (bonusData: BonusData) => void;
  storedSubmission?: any;
  contentFlags?: ContentFlags;
  chatSession?: ChatSession;
  onChatSessionUpdate?: (session: ChatSession) => void;
  onChatSessionReset?: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5444';

const AnswerSubmission: React.FC<AnswerSubmissionProps> = ({ dayNumber, isCurrentDay, isSpecialDay = false, companyGameId, totalAvailableDays = 25, availableDays = [], onServerAnswer, storedSubmission, contentFlags, chatSession, onChatSessionUpdate, onChatSessionReset }) => {
  const [promptUsed, setPromptUsed] = useState('');
  const [response, setResponse] = useState('');
  const [transcript, setTranscript] = useState('');
  const [feedback, setFeedback] = useState<{ message: string; isCorrect: boolean } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnsweredCorrectly, setIsAnsweredCorrectly] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const feedbackRef = useRef<HTMLDivElement>(null);

  // Default content flags if not provided
  const flags: ContentFlags = contentFlags || {
    response: true,
    promptUsed: true,
    files: true,
    transcript: false
  };

  // Removed autofocus to prevent mobile scroll issues

  // Scroll to feedback when it appears
  useEffect(() => {
    if (feedback && feedbackRef.current) {
      // Find the modal-body container and scroll it to bottom
      const modalBody = feedbackRef.current.closest('.modal-body');
      modalBody?.scrollTo({
        top: modalBody.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [feedback]);

  const handleFileUploadChange = (event: any) => {
    console.log('Files changed:', event);
    setUploadedFiles(event.allFiles || []);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate based on content flags
    if (flags.promptUsed && !promptUsed.trim()) {
      setFeedback({ message: 'Please enter the prompt you used', isCorrect: false });
      return;
    }

    if (flags.response && !response.trim() && (!flags.files || uploadedFiles.length === 0)) {
      setFeedback({ message: 'Please enter the response or attach a file', isCorrect: false });
      return;
    }

    if (flags.transcript && !transcript.trim()) {
      setFeedback({ message: 'Please enter the AI transcript', isCorrect: false });
      return;
    }

    sound.clickOpen();
    setIsSubmitting(true);
    setFeedback(null);

    // Track response submission
    trackSubmitResponse({
      day: dayNumber,
      today: isCurrentDay,
      best_day_score: getDayScore(dayNumber),
      best_day_stars: getDayStars(dayNumber),
      total_score: getScore(),
      total_stars: getTotalStars()
    });

    // TEST: Wait 2 seconds to test spinner
    // await new Promise(resolve => setTimeout(resolve, 2000));

    // TEST immediate error response
    // setFeedback({
    //   message: 'Error checking answer. Please try again.',
    //   isCorrect: false,
    // });
    // setIsSubmitting(false);

    try {
      const body: any = {
        day: dayNumber,
        companyGameId: companyGameId,
        today: isCurrentDay,
      };

      // Only include fields that are enabled by content flags
      if (flags.promptUsed) {
        body.promptUsed = promptUsed.trim();
      }
      if (flags.response) {
        body.response = response.trim();
      }
      if (flags.transcript) {
        body.transcript = transcript.trim();
      }
      if (flags.files) {
        body.files = uploadedFiles?.filter(file => file.status === "success")
          .map(file => ({
            name: file.name,
            url: file.cdnUrl,
            type: file.type
          }));
      }

      // console.log('AnswerSubmission: Submitting answer with body:', body);

      const apiResponse = await fetch(`${API_URL}/api/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      // Check for authentication errors
      if (apiResponse.status === 401 || apiResponse.status === 403) {
        console.error('[Auth] Token expired or invalid, reloading page...');
        window.location.reload();
        return;
      }

      const responseData = await apiResponse.json();
      let data = responseData.data || responseData;

      // TEST override DEBUG
      // data = {
      //   stars: 3,
      //   score: 5000,
      //   scoreBreakdown: {
      //     relevance: 100,
      //     correctness: 100,
      //     depth: 100,
      //     clarity: 100,
      //     promptcraft: 100
      //   },
      //   feedback: {
      //     improvements: ['Improvement 1', 'Improvement 2'],
      //     strengths: ['Strength 1', 'Strength 2'],
      //     summary: 'test '.repeat(150)
      //   }
      // };

      console.log('AnswerSubmission: API response data:', responseData);

      // Get star rating, score, and breakdown from API response
      const stars = data.stars || 0;
      const isComplete = stars >= 1;

      // Fix in case API returns score > 0 with stars=0
      if (stars === 0)
        data.score = 0;

      // If no stars, override score to 0
      const totalPoints = data.score || 0;
      const scoreBreakdown = data.scoreBreakdown || {
        relevance: 100,
        correctness: 100,
        depth: 100,
        clarity: 100,
        promptcraft: 100
      };

      // Mark as answered correctly if there's at least one star
      if (isComplete) {
        setIsAnsweredCorrectly(true);
      }

      // Check if day was already completed before saving
      const wasAlreadyCompleted = isDayCompleted(dayNumber);

      // Determine if this is an on-time completion (completed on the current day)
      const isOnTime = isCurrentDay;

      let result: { scoreDelta: number; previousBreakdown: ScoreBreakdown | null } = {
        scoreDelta: totalPoints,
        previousBreakdown: null
      };

      // Only save completion if there's at least one star
      if (isComplete) {
        result = markDayCompleted(dayNumber, totalPoints, stars, scoreBreakdown, isOnTime, data.feedback);
        // Reset chat session on successful submission
        if (onChatSessionReset) {
          onChatSessionReset();
        }
      } else {
        // Save submission data even for 0-star submissions
        saveSubmissionData(dayNumber, totalPoints, stars, scoreBreakdown, data.feedback);
      }

      // Determine if this is an improvement (resubmission with better score)
      const isImprovement = wasAlreadyCompleted && result.scoreDelta > 0;

      // Always return the score data and show popup
      const bonusData: BonusData = {
        totalPoints,
        stars,
        scoreBreakdown,
        scoreDelta: result.scoreDelta,
        addedPoints: result.scoreDelta, // The points actually added to total
        isImprovement,
        previousBreakdown: result.previousBreakdown,
        feedback: data.feedback
      };

      if (onServerAnswer) {
        onServerAnswer(bonusData);
      }
    } catch (error) {
      console.error('Error validating answer:', error);
      setFeedback({
        message: 'Error checking answer. Please try again.',
        isCorrect: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`answer-submission ${isSpecialDay ? 'special-day' : ''}`}>
      <form onSubmit={handleSubmit}>
        <div className="answer-fields">
          {flags.promptUsed && (
            <div className="field-group">
              <label htmlFor="promptUsed" className="field-label">
                Prompt Used
              </label>
              <textarea
                id="promptUsed"
                value={promptUsed}
                onChange={(e) => {
                  setPromptUsed(e.target.value);
                  setFeedback(null);
                  setIsAnsweredCorrectly(false);
                }}
                placeholder="Enter the prompt you used with the AI..."
                className="answer-textarea"
                disabled={isSubmitting}
                rows={3}
              />
            </div>
          )}          
          {flags.transcript && (
            <div className="field-group">
              <label htmlFor="transcript" className="field-label field-label-center">
                AI Chat
              </label>
              <ChatInterface
                dayNumber={dayNumber}
                companyGameId={companyGameId}
                isSpecialDay={isSpecialDay}
                onTranscriptUpdate={(transcriptText) => {
                  setTranscript(transcriptText);
                  setFeedback(null);
                  setIsAnsweredCorrectly(false);
                }}
                disabled={isSubmitting}
                messages={chatSession?.messages}
                conversationId={chatSession?.conversationId}
                onMessagesUpdate={(messages) => {
                  if (onChatSessionUpdate && chatSession) {
                    onChatSessionUpdate({ ...chatSession, messages });
                  } else if (onChatSessionUpdate) {
                    onChatSessionUpdate({ messages, conversationId: chatSession?.conversationId || null });
                  }
                }}
                onConversationIdUpdate={(conversationId) => {
                  if (onChatSessionUpdate) {
                    onChatSessionUpdate({ messages: chatSession?.messages || [], conversationId });
                  }
                }}
              />
            </div>
          )}          
          {flags.response && (
            <div className="field-group">
              <label htmlFor="response" className="field-label">
                Response
              </label>
              <textarea
                id="response"
                value={response}
                onChange={(e) => {
                  setResponse(e.target.value);
                  setFeedback(null);
                  setIsAnsweredCorrectly(false);
                }}
                placeholder="Enter the AI's response..."
                className="answer-textarea"
                disabled={isSubmitting}
                rows={3}
              />
            </div>
          )}
          {flags.files && (
            <div className="field-group file-upload-field">
              <SimpleFileUpload
                accept='image/*'
                multiple={true}
                publicKey={process.env.REACT_APP_SIMPLE_FILE_UPLOAD_KEY || ''}
                onChange={handleFileUploadChange}
              />
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files-list">
                  {uploadedFiles.map((file: any, index: number) => (
                    <div key={index} className="uploaded-file-item">
                      <span className="file-checkmark">✓</span>
                      <span className="file-name">{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {!feedback && (
            <button
              type="submit"
              className={`submit-button ${isSpecialDay ? 'special-day' : ''}`}
              disabled={
                isSubmitting ||
                isAnsweredCorrectly ||
                (flags.promptUsed && !promptUsed.trim()) ||
                (flags.response && !response.trim() && (!flags.files || uploadedFiles.length === 0)) ||
                (flags.transcript && !transcript.trim())
              }
            >
              {isSubmitting ? (
                <>
                  <div className="answer-spinner"></div>
                  Checking...
                </>
              ) : (
                flags.transcript && !flags.response && !flags.promptUsed && !flags.files
                  ? 'Submit AI Transcript'
                  : 'Submit Answer'
              )}
            </button>
          )}
        </div>
        {feedback && (
          <div
            ref={feedbackRef}
            className={`submission-feedback ${feedback.isCorrect ? 'correct' : 'incorrect'}`}
          >
            {feedback.message}
          </div>
        )}
      </form>
    </div>
  );
};

export default AnswerSubmission;
